/**
 * Chart Data Building Helpers
 *
 * These helpers build chart data for different time ranges:
 * - 1 Day, 1 Week, 1 Month, 6 Months, 1 Year, Custom Date
 */

import { subDays, format, startOfMonth, subMonths } from "date-fns";

import { BloodPressureData } from "./bloodPressureHelpers";

export interface ChartDataPoint {
  date: string;
  systolic: number;
  diastolic: number;
  isCurrent: boolean;
}

/**
 * Build chart data for a single day
 * @param today - Current date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with single day's data
 */
export const buildDayData = (
  today: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const key = format(today, "yyyy-MM-dd");
  const bpData = dataMap[key] || { systolic: 0, diastolic: 0 };
  return [
    {
      date: format(today, "MMM d, yyyy"),
      systolic: bpData.systolic,
      diastolic: bpData.diastolic,
      isCurrent: true,
    },
  ];
};

/**
 * Build chart data for one week (7 days)
 * @param today - Current date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with 7 days of data
 */
export const buildWeekData = (
  today: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const date = subDays(today, i);
    const key = format(date, "yyyy-MM-dd");
    const bpData = dataMap[key] || { systolic: 0, diastolic: 0 };
    days.push({
      date: format(date, "EEE").slice(0, 2), // Mo, Tu, We...
      systolic: bpData.systolic,
      diastolic: bpData.diastolic,
      isCurrent: i === 0,
    });
  }
  return days;
};

/**
 * Build chart data for one month (31 days)
 * @param today - Current date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with 31 days of data
 */
export const buildMonthData = (
  today: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const days = [];
  for (let i = 30; i >= 0; i--) {
    const date = subDays(today, i);
    const key = format(date, "yyyy-MM-dd");
    const bpData = dataMap[key] || { systolic: 0, diastolic: 0 };
    days.push({
      date: format(date, "dd"),
      systolic: bpData.systolic,
      diastolic: bpData.diastolic,
      isCurrent: i === 0,
    });
  }
  return days;
};

/**
 * Calculate monthly average blood pressure values
 * @param dataMap - Map of date keys to blood pressure data
 * @param monthKey - Month key in format "yyyy-MM"
 * @returns Average systolic and diastolic values for the month
 */
export const calculateMonthlyAverage = (
  dataMap: Record<string, BloodPressureData>,
  monthKey: string,
): { avgSystolic: number; avgDiastolic: number } => {
  const monthData = Object.keys(dataMap)
    .filter((d) => d.startsWith(monthKey))
    .map((k) => dataMap[k])
    .filter((bp) => bp.systolic > 0 || bp.diastolic > 0);

  const avgSystolic =
    monthData.length > 0
      ? monthData.reduce((sum, bp) => sum + bp.systolic, 0) / monthData.length
      : 0;
  const avgDiastolic =
    monthData.length > 0
      ? monthData.reduce((sum, bp) => sum + bp.diastolic, 0) / monthData.length
      : 0;

  return { avgSystolic, avgDiastolic };
};

/**
 * Build chart data for 6 months (monthly averages)
 * @param today - Current date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with 6 months of averaged data
 */
export const build6MonthData = (
  today: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const date = subMonths(today, i);
    const key = format(startOfMonth(date), "yyyy-MM");
    const { avgSystolic, avgDiastolic } = calculateMonthlyAverage(dataMap, key);

    months.push({
      date: format(date, "MMM"),
      systolic: avgSystolic,
      diastolic: avgDiastolic,
      isCurrent: i === 0,
    });
  }
  return months;
};

/**
 * Build chart data for 1 year (12 months of monthly averages)
 * @param today - Current date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with 12 months of averaged data
 */
export const buildYearData = (
  today: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const date = subMonths(today, i);
    const key = format(startOfMonth(date), "yyyy-MM");
    const { avgSystolic, avgDiastolic } = calculateMonthlyAverage(dataMap, key);

    months.push({
      date: format(date, "MMM").charAt(0),
      systolic: avgSystolic,
      diastolic: avgDiastolic,
      isCurrent: i === 0,
    });
  }
  return months;
};

/**
 * Build chart data for a custom selected date
 * @param selectedDate - The selected date
 * @param dataMap - Map of date keys to blood pressure data
 * @returns Array with single day's data for the selected date
 */
export const buildCustomData = (
  selectedDate: Date,
  dataMap: Record<string, BloodPressureData>,
): ChartDataPoint[] => {
  const key = format(selectedDate, "yyyy-MM-dd");
  const bpData = dataMap[key] || { systolic: 0, diastolic: 0 };
  return [
    {
      date: format(selectedDate, "MMM d, yyyy"),
      systolic: bpData.systolic,
      diastolic: bpData.diastolic,
      isCurrent: true,
    },
  ];
};
